/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { DialogCloseEvent, DialogComponent, unlockBodyScroll } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { hideSizeRequiredMessage } from '@theme/product-form';

/**
 * Global singleton Quick View drawer/modal (Compatible Protection / Complete
 * the Gear carousels). Content is cloned in by `QuickViewTrigger`, not
 * fetched — see snippets/quick-view-content.liquid.
 */
class QuickViewDrawer extends DialogComponent {
  #cartUpdateAbortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Bound to this element, not the document, like the Quick Add dialog: the
    // form lives inside the drawer, so this runs while the event is still
    // bubbling — before the cart drawer's own document listener opens it.
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cartUpdateAbortController.abort();
  }

  /**
   * Replaces the drawer's content and opens it.
   * @param {DocumentFragment} content
   */
  open(content) {
    const target = document.getElementById('quick-view-drawer-content');
    if (!target) return;

    target.replaceChildren(content);
    this.showDialog();
  }

  /**
   * Successful Add to cart closes the Quick View (9.1.2) — a cart error
   * leaves it open so the shopper can retry.
   * @param {import('./events').CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;

    this.#closeInstantly();
  };

  /**
   * `closeDialog` only calls `dialog.close()` once the closing animation has
   * finished. The cart drawer opens from this same event, so waiting would leave
   * two modal dialogs stacked. Close right away and hand the scroll lock over.
   */
  #closeInstantly() {
    const { dialog } = this.refs;

    if (!dialog?.open) return;

    dialog.classList.remove('dialog-closing');
    dialog.close();

    unlockBodyScroll();

    this.dispatchEvent(new DialogCloseEvent());
  }
}

if (!customElements.get('quick-view-drawer')) {
  customElements.define('quick-view-drawer', QuickViewDrawer);
}

/**
 * Wraps each cross-sell card's "Add to cart" button. Clones the sibling
 * `<template data-quick-view-template>` content into the shared drawer and
 * opens it — no page fetch, the content is already server-rendered.
 */
class QuickViewTrigger extends Component {
  /** @param {Event} event */
  handleClick(event) {
    event.preventDefault();

    const card = this.closest('product-card');
    const template = card?.querySelector('template[data-quick-view-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const drawer = document.getElementById('quick-view-drawer');
    if (!(drawer instanceof QuickViewDrawer)) return;

    const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));

    wireSizeSelection(content);
    fillReturnsRegulations(content);

    if (this.dataset.quickViewTechAir === 'true') {
      applyProtectorSizeSuggestion(content, this.closest('protection-upsell'));
    }

    drawer.open(content);
  }
}

if (!customElements.get('quick-view-trigger')) {
  customElements.define('quick-view-trigger', QuickViewTrigger);
}

/**
 * Enables the Add to cart button once an available size is picked, and keeps
 * the hidden variant id input in sync. This Quick View doesn't run the PDP's
 * AJAX variant-picker, so nothing else does this here.
 *
 * @param {DocumentFragment} content - The cloned Quick View content, not yet inserted into the document.
 */
/**
 * The "Show Returns regulations" drawer text is a setting of the PDP's inventory
 * notice block, which the Quick View's own render cannot reach. The block publishes
 * it in a `<template data-techair-returns-content>`; copy it into any drawer that
 * came through empty.
 * @param {DocumentFragment} content
 */
function fillReturnsRegulations(content) {
  const source = document.querySelector('template[data-techair-returns-content]');
  if (!(source instanceof HTMLTemplateElement)) return;

  for (const target of content.querySelectorAll('.variant-inventory-notice__drawer-content')) {
    if (target.textContent?.trim()) continue;
    target.replaceChildren(source.content.cloneNode(true));
  }
}

function wireSizeSelection(content) {
  const fieldset = content.querySelector('fieldset.variant-option--grid');
  const variantIdInput = content.querySelector('[ref="variantId"]');
  if (!fieldset || !(variantIdInput instanceof HTMLInputElement)) return;

  // A product with no size in stock renders Notify Me alone, without an Add to
  // cart button. The picked size must still reach the hidden variant id, or
  // Notify Me cannot tell which size was chosen and asks for it again.
  const addToCartButton = content.querySelector('[ref="addToCartButton"]');
  // A sold-out size shows Notify Me in place of Add to cart (and back). The
  // markup renders both; see snippets/quick-view-content.liquid.
  const notifyMe = content.querySelector('[data-quick-view-notify]');
  const addToCart = addToCartButton?.closest('add-to-cart-component');
  fieldset.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    variantIdInput.value = target.dataset.variantId || '';
    const soldOut = target.dataset.optionAvailable !== 'true';
    if (addToCartButton instanceof HTMLButtonElement) addToCartButton.disabled = soldOut;
    if (notifyMe instanceof HTMLElement && addToCart instanceof HTMLElement) {
      notifyMe.hidden = !soldOut;
      addToCart.hidden = soldOut;
    }
    hideSizeRequiredMessage(fieldset);
  });
}

/**
 * Compatible Protection Quick View — Tech-Air sizing suggestion (9.4).
 *
 * Reads the main PDP's currently selected size and, when a mapped protector
 * size is available, injects a single informational line under the size
 * fieldset's legend. Left untouched when the main product has no size
 * selected yet, or no mapping applies (9.4.1) — purely informational, the
 * shopper still picks the size manually (9.4.2).
 *
 * @param {DocumentFragment} content - The cloned Quick View content, not yet inserted into the document.
 * @param {Element | null} protectionUpsell - The `<protection-upsell>` ancestor carrying the mapping and the suggestion's translated text.
 */
function applyProtectorSizeSuggestion(content, protectionUpsell) {
  // The mapping is rendered into the content only when `quick-view-content` is
  // called with `protection_mapping`. Card CTAs added as custom Liquid blocks
  // can't pass it, so fall back to the mapping on the host element.
  const root = content.querySelector('[data-protector-size-mapping]');
  const mappingAttr =
    root?.getAttribute('data-protector-size-mapping') || protectionUpsell?.getAttribute('data-protection-mapping');
  const textTemplate = protectionUpsell?.getAttribute('data-protector-size-suggestion-text');
  if (!mappingAttr || !textTemplate) return;

  const mainSizeInput = document.querySelector(
    '.product-information fieldset.variant-option--grid input[type="radio"]:checked',
  );
  const mainSize = mainSizeInput instanceof HTMLInputElement ? mainSizeInput.value : '';
  if (!mainSize) return;

  /** @type {Map<string, string>} */
  const mapping = new Map(
    mappingAttr
      .split(',')
      .map((pair) => /** @type {[string, string]} */ (pair.split(':').map((part) => part.trim())))
      .filter((pair) => pair[0] && pair[1]),
  );

  const suggestedSize = mapping.get(mainSize);
  if (!suggestedSize) return;

  const legend = content.querySelector('fieldset.variant-option--grid legend');
  if (!legend) return;

  const suggestion = document.createElement('p');
  suggestion.setAttribute('data-protector-size-suggestion', '');
  suggestion.textContent = textTemplate.replace('__SIZE__', suggestedSize);
  legend.insertAdjacentElement('afterend', suggestion);
}
